/** Assignment dates are plain calendar dates, so everything is handled in UTC. */

export function toIsoDate(date: Date): string {
    return date.toISOString().slice(0, 10);
}

export function todayIso(): string {
    return toIsoDate(new Date());
}

export function fromIsoDate(value: string): Date {
    return new Date(`${value}T00:00:00.000Z`);
}

export function addDays(value: string, days: number): string {
    const date = fromIsoDate(value);
    date.setUTCDate(date.getUTCDate() + days);
    return toIsoDate(date);
}

/** Monday of the week containing `value`. */
export function startOfWeek(value: string): string {
    const date = fromIsoDate(value);
    const day = date.getUTCDay();
    const diff = day === 0 ? -6 : 1 - day;
    date.setUTCDate(date.getUTCDate() + diff);
    return toIsoDate(date);
}

export function isSameDay(a: string, b: string): boolean {
    return a === b;
}

export function isToday(value: string): boolean {
    return value === todayIso();
}

export function weekDates(monday: string): string[] {
    return Array.from({ length: 7 }, (_, index) => addDays(monday, index));
}

const DAY_FORMATTER = new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    timeZone: "UTC",
});

const DAY_NUMBER_FORMATTER = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    timeZone: "UTC",
});

const LONG_DATE_FORMATTER = new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
});

export function formatWeekday(value: string): string {
    return DAY_FORMATTER.format(fromIsoDate(value));
}

export function formatDayMonth(value: string): string {
    return DAY_NUMBER_FORMATTER.format(fromIsoDate(value));
}

export function formatLongDate(value: string): string {
    return LONG_DATE_FORMATTER.format(fromIsoDate(value));
}

export function formatTime(value: string): string {
    const [hour, minute] = value.split(":");
    return `${hour}:${minute}`;
}

export function formatTimeRange(start: string, end: string): string {
    return `${formatTime(start)} – ${formatTime(end)}`;
}

export function minutesBetween(start: string, end: string): number {
    const toMinutes = (value: string) => {
        const [h, m] = value.split(":").map(Number);
        return h * 60 + m;
    };
    const s = toMinutes(start);
    const e = toMinutes(end);
    return e >= s ? e - s : e + 24 * 60 - s;
}

export function formatDuration(minutes: number): string {
    if (minutes <= 0) return "0m";
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (hours === 0) return `${rest}m`;
    if (rest === 0) return `${hours}h`;
    return `${hours}h ${rest}m`;
}

export function formatRangeLabel(from: string, to: string): string {
    if (from === to) return formatLongDate(from);
    return `${formatDayMonth(from)} – ${formatDayMonth(to)}`;
}
